import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getQuizHistory, GetQuizHistoryOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { CheckCircle2, XCircle, Download, ChevronDown, ChevronUp, Brain, Trophy, Zap, Star, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { buildQuizResultHtml, openPrintWindow, type PrintQuestion, type PrintAnswer } from '@/lib/quizPrint';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { toast } from 'sonner';

type Attempt = GetQuizHistoryOutputType['attempts'][0];

function scoreColor(pct: number) {
  return pct >= 80 ? 'text-green-600' : pct >= 50 ? 'text-yellow-600' : 'text-red-500';
}
function scoreBg(pct: number) {
  return pct >= 80 ? 'bg-green-500/10 border-green-500/30' : pct >= 50 ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-red-500/10 border-red-500/30';
}
function scoreEmoji(pct: number) {
  return pct === 100 ? '🏆' : pct >= 80 ? '🎉' : pct >= 50 ? '👍' : '📚';
}
function timeAgo(d?: string) {
  if (!d) return '';
  try { return formatDistanceToNow(parseISO(d), { addSuffix: true }); } catch { return ''; }
}

function isCorrectAnswer(q: PrintQuestion, a: PrintAnswer): boolean {
  if (Array.isArray(q.correct)) {
    const ua = Array.isArray(a.answer) ? a.answer as number[] : [a.answer as number];
    return JSON.stringify([...(q.correct as number[])].sort()) === JSON.stringify([...ua].sort());
  }
  return a.answer === q.correct;
}

function AttemptCard({ attempt }: { attempt: Attempt }) {
  const [expanded, setExpanded] = useState(false);

  const questions: PrintQuestion[] = (() => {
    try { return JSON.parse(attempt.questionsJson); } catch { return []; }
  })();
  const answersArr: PrintAnswer[] = (() => {
    try { return JSON.parse(attempt.answersJson); } catch { return []; }
  })();

  const handleDownload = () => {
    if (questions.length === 0) { toast.error('No question data available for this attempt'); return; }
    const html = buildQuizResultHtml({
      sectionTitle: attempt.sectionTitle,
      completedAt: attempt.completedAt,
      score: attempt.score,
      total: attempt.totalQuestions,
      percentage: attempt.percentage,
      questions,
      answersArr,
    });
    openPrintWindow(html);
  };

  return (
    <div className={cn('border rounded-xl overflow-hidden transition-all', scoreBg(attempt.percentage))}>
      {/* Header row */}
      <div className="px-4 py-3.5 flex items-center gap-3">
        <div className="text-2xl shrink-0">{scoreEmoji(attempt.percentage)}</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{attempt.sectionTitle}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {timeAgo(attempt.completedAt)} · {attempt.score}/{attempt.totalQuestions} correct
            {attempt.pointsEarned > 0 && <span className="ml-2 text-primary font-medium">+{attempt.pointsEarned} pts</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={cn('text-xl font-bold', scoreColor(attempt.percentage))}>{attempt.percentage}%</span>
          <Button variant="ghost" size="sm" className="w-8 h-8 p-0" onClick={() => setExpanded(v => !v)}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 text-xs h-7 px-2.5" onClick={handleDownload}>
            <Download className="w-3 h-3" /> Download
          </Button>
        </div>
      </div>

      {/* Expanded Q&A */}
      {expanded && (
        <div className="border-t border-border bg-card">
          {questions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No question data available for this attempt.</p>
          ) : (
            <div className="p-4 space-y-3">
              {questions.map((q, idx) => {
                const ans = answersArr.find(a => a.id === q.id);
                const userAnsArr = !ans ? [] : Array.isArray(ans.answer) ? ans.answer as number[] : [ans.answer as number];
                const correctArr = Array.isArray(q.correct) ? q.correct as number[] : [q.correct as number];
                const correct = ans ? isCorrectAnswer(q, ans) : false;

                return (
                  <div key={q.id} className={cn('border rounded-lg overflow-hidden', correct ? 'border-green-200' : 'border-red-200')}>
                    {/* Question header */}
                    <div className={cn('px-3 py-2 flex items-start gap-2', correct ? 'bg-green-50/60' : 'bg-red-50/60')}>
                      {correct
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 mt-0.5 shrink-0" />
                        : <XCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 shrink-0" />}
                      <p className="text-sm font-medium text-foreground">
                        <span className="text-muted-foreground mr-1.5">Q{idx + 1}.</span>{q.question}
                      </p>
                    </div>

                    {/* Options */}
                    <div className="px-3 py-2.5 space-y-1">
                      {q.options.map((opt, i) => {
                        const isRight = correctArr.includes(i);
                        const wasSelected = userAnsArr.includes(i);
                        return (
                          <div key={i} className={cn('flex items-center gap-2 px-2 py-1 rounded text-xs',
                            isRight ? 'bg-green-100 text-green-800 font-semibold' :
                            wasSelected ? 'bg-red-100 text-red-700 font-medium' : 'text-muted-foreground')}>
                            <span className="text-sm shrink-0">{isRight ? '✓' : wasSelected ? '✗' : '·'}</span>
                            <span className="flex-1">{opt}</span>
                            {isRight && <span className="text-green-700 font-medium ml-1">correct answer</span>}
                            {wasSelected && !isRight && <span className="text-red-600 ml-1">your answer</span>}
                          </div>
                        );
                      })}
                    </div>

                    {/* Explanation */}
                    {q.explanation && (
                      <div className="mx-3 mb-2.5 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                        <p className="text-xs font-semibold text-blue-700 mb-0.5">💡 Why:</p>
                        <p className="text-xs text-blue-900 leading-relaxed">{q.explanation}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function QuizHistory() {
  const { user, isLoading, loginWithRedirect } = useAuth();
  const [data, setData] = useState<GetQuizHistoryOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isLoading && !user) loginWithRedirect({ redirectUrl: window.location.href });
  }, [isLoading, user, loginWithRedirect]);

  useEffect(() => {
    if (!user) return;
    getQuizHistory({})
      .then(d => setData(d))
      .catch(() => toast.error('Failed to load quiz history'))
      .finally(() => setLoading(false));
  }, [user]);

  if (isLoading || !user) return null;

  const filtered = (data?.attempts ?? []).filter(a =>
    !search || a.sectionTitle.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Brain className="w-6 h-6 text-primary" /> My Quiz History
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            All your quiz attempts with full answers, correct answers, and explanations.
          </p>
        </div>
      </div>

      {/* Stats bar */}
      {data && data.totalAttempts > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: <Brain className="w-4 h-4" />, value: data.totalAttempts, label: 'Total Attempts' },
            { icon: <Star className="w-4 h-4" />, value: `${data.averagePercentage}%`, label: 'Average Score' },
            { icon: <Zap className="w-4 h-4" />, value: data.attempts.reduce((s, a) => s + a.pointsEarned, 0), label: 'Points from Quizzes' },
          ].map((stat, i) => (
            <div key={i} className="border border-border rounded-xl bg-card p-4 flex flex-col items-center gap-1 text-center">
              <div className="text-primary">{stat.icon}</div>
              <p className="text-xl font-bold text-foreground">{stat.value}</p>
              <p className="text-xs text-muted-foreground">{stat.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      {data && data.totalAttempts > 3 && (
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search by section name..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      )}

      {/* Attempts list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center py-20 text-center gap-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Brain className="w-8 h-8 text-primary" />
          </div>
          <div>
            <p className="text-lg font-semibold text-foreground">
              {search ? 'No results found' : 'No quiz attempts yet'}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {search ? 'Try a different search term.' : 'Head to the Handbook and take a section quiz to get started!'}
            </p>
          </div>
          {!search && (
            <Button onClick={() => window.location.href = '/handbook'}>
              📖 Go to Handbook
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
            {filtered.length} attempt{filtered.length !== 1 ? 's' : ''}{search ? ` matching "${search}"` : ''}
          </p>
          {filtered.map(a => <AttemptCard key={a.id} attempt={a} />)}
        </div>
      )}
    </div>
  );
}
